'use client';

import { InternetCard } from '@modules/get-slash-internet-card';
import { XiCafeInternetCard } from '@modules/xi-cafe/components/xi-cafe-internet-card';
import { DashboardCard } from '@tuic/web-compositions/shared/DashboardCard';

import { WithExplanationVideoDialog } from '../../../../../app/[locale]/[bookingCode]/_components/with-explanation-video';
import { ChatCard } from '../../../../../app/[locale]/_components/chat-card';
import { Link } from '../../elements/Link';

import { NewInformationTag } from './NewInformationTag';

import type { ExplanationVideoOptions } from '../../../../../app/[locale]/[bookingCode]/_components/with-explanation-video';
import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { Locale } from '@tuic/i18n';
import type { DashboardCardProps } from '@tuic/web-compositions/shared/DashboardCard';
import type { ComponentPropsWithoutRef } from 'react';

type GetSlashInternetCardProps = ComponentPropsWithoutRef<typeof InternetCard>;
type XiCafeInternetCardProps = ComponentPropsWithoutRef<
  typeof XiCafeInternetCard
>;

/**
 * Internet card variant on a Prism card block. Discriminated on `provider`;
 * the renderer below dispatches to either the GetSlash or XiCafe component.
 */
type InternetCardData =
  | ({ provider: 'getSlash' } & GetSlashInternetCardProps)
  | ({ provider: 'xiCafe' } & XiCafeInternetCardProps);

export type CardBlockData = {
  card?: DashboardCardProps;
  link?: PrismAPI.LinkElement;
  explanationVideo?: ExplanationVideoOptions;
  internetCard?: InternetCardData;
  chatCard?: PrismAPI.CardChatCard;
  newInformationTag?: PrismAPI.CardNewInformationTag;
  locale?: Locale;
};

export const CardBlock: ComponentBlock<CardBlockData> = ({
  data,
  fallback,
}) => {
  if (fallback) {
    return <DashboardCard fallback />;
  }

  const {
    link,
    explanationVideo,
    internetCard,
    chatCard,
    newInformationTag,
    locale,
  } = data ?? {};

  const card = createCardWithNewInformationTag(
    locale,
    data?.card,
    newInformationTag,
  );

  if (explanationVideo) {
    return (
      <WithExplanationVideoDialog {...explanationVideo}>
        {open => (
          <DashboardCard {...card} asChild>
            <button onClick={open} />
          </DashboardCard>
        )}
      </WithExplanationVideoDialog>
    );
  }

  if (internetCard) {
    if (internetCard.provider === 'xiCafe') {
      const { provider: _provider, ...xiCafeProps } = internetCard;
      return <XiCafeInternetCard {...xiCafeProps} dashboardCard={card} />;
    }
    const { provider: _provider, ...getSlashProps } = internetCard;
    return <InternetCard {...getSlashProps} dashboardCard={card} />;
  }

  if (chatCard) {
    if (link) {
      return (
        <ChatCard
          {...chatCard}
          dashboardCard={{
            ...card,
            asChild: true,
            children: <Link data={link} />,
          }}
        />
      );
    }

    return <ChatCard {...chatCard} dashboardCard={card} />;
  }

  if (link) {
    return (
      <DashboardCard {...card} asChild>
        <Link data={link} />
      </DashboardCard>
    );
  }

  return <DashboardCard {...card} />;
};

/**
 * Create a card with NewInformationTag if newInformationTag data exists
 */
const createCardWithNewInformationTag = (
  locale: Locale | undefined,
  card: DashboardCardProps | undefined,
  newInformationTag: PrismAPI.CardNewInformationTag | undefined,
): DashboardCardProps | undefined => {
  if (!card || !newInformationTag || !locale) {
    return card;
  }

  return {
    ...card,
    tag: (
      <NewInformationTag
        newInformationTag={newInformationTag}
        originalTag={card.tag}
        locale={locale}
      />
    ),
  };
};
